import { useMemo } from 'react';
import { FolderOpen } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import { useCollections } from '../../hooks/useCollections';

function format(n: number) {
  return n.toLocaleString('pt-BR');
}

interface Props {
  collectionId: string;
}

/** Faixa de contexto da coleção aberta: nome + descrição + métricas próprias. */
export function CollectionHeader({ collectionId }: Props) {
  const { data: collections } = useCollections();
  const col = useMemo(
    () => collections?.find((c) => c.collection_id === collectionId),
    [collections, collectionId],
  );

  return (
    // px-6 py-4: mesmo gutter da tabela abaixo, pra nome da coleção e nome do
    // arquivo caírem na mesma coluna vertical.
    <div className="flex shrink-0 items-center gap-3 border-b px-6 py-4">
      <div className="bg-accent flex size-9 shrink-0 items-center justify-center rounded-lg">
        <FolderOpen className="text-muted-foreground size-4.5" />
      </div>
      <div className="min-w-0 flex-1">
        <h2 className="truncate text-base leading-tight font-semibold" title={collectionId}>
          {collectionId}
        </h2>
        <CollectionDescription description={col?.description} />
      </div>
      {col && (
        <div className="text-muted-foreground hidden shrink-0 items-center gap-4 text-xs sm:flex">
          <Metric value={format(col.indexed)} label="indexados" />
          {col.processing > 0 && (
            <Metric value={format(col.processing)} label="processando" tone="amber" />
          )}
          {col.failed > 0 && <Metric value={format(col.failed)} label="falhas" tone="red" />}
          <Metric value={format(col.total_chunks)} label="chunks" />
        </div>
      )}
    </div>
  );
}

/**
 * Descrição da coleção, truncada na faixa e inteira no hover.
 *
 * A descrição é o que o assistente usa para escolher a coleção ao responder,
 * então costuma ser longa; truncada e sem nenhuma forma de ler o resto, o texto
 * cadastrado na criação ficava inacessível.
 */
function CollectionDescription({ description }: { description: string | undefined }) {
  const text = description?.trim();

  if (!text) {
    return <p className="text-muted-foreground mt-0.5 truncate text-sm">Sem descrição.</p>;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <p className="text-muted-foreground mt-0.5 w-fit max-w-full truncate text-sm">{text}</p>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm whitespace-pre-wrap">{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function Metric({ value, label, tone }: { value: string; label: string; tone?: 'amber' | 'red' }) {
  const color =
    tone === 'red' ? 'text-destructive' : tone === 'amber' ? 'text-amber-600' : 'text-foreground';
  return (
    <span className="flex items-baseline gap-1">
      <span className={`text-sm font-semibold tabular-nums ${color}`}>{value}</span>
      {label}
    </span>
  );
}
